import { useState } from "react";
import classNames from "classnames";
import { useMutation } from "react-query";
import { NavLink } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { reactQueryDelete } from "@circle-react/helpers/backendRequestHelpers";
import { formatDateString } from "@circle-react/helpers/dateTimeHelpers/getLocalizedDate";
import { coursePaths, internalApi } from "@circle-react/helpers/urlHelpers";
import { useAnyHoverMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { UserImage } from "@circle-react-shared/uikit/UserImage";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import type { DropdownOptions } from "@circle-react-uikit/Dropdown";
import { Typography } from "@circle-react-uikit/Typography";

interface CommentItemProps {
  comment: any;
  refetch: () => void;
  space: any;
}

export const CommentItem = ({ comment, space, refetch }: CommentItemProps) => {
  const [hasDeleteConfirmationModal, setHasDeleteConfirmationModal] =
    useState(false);

  const closeDeleteConfirmationModal = () =>
    setHasDeleteConfirmationModal(false);

  const canHover = useAnyHoverMediaQuery();

  const dropDownOptions: DropdownOptions = [
    {
      value: "delete-message",
      label: <span className="text-v2-danger">{t("delete")}</span>,
      onClick: () => setHasDeleteConfirmationModal(true),
    },
  ];

  const deleteCommentMutation = useMutation(
    () =>
      reactQueryDelete(
        internalApi.chatRoomMessages.delete({
          uuid: comment.chat_room_uuid,
          id: comment.id,
        }),
      ),
    {
      onSuccess: () => {
        refetch();
      },
    },
  );

  return (
    <>
      <ConfirmationModal
        isOpen={hasDeleteConfirmationModal}
        onClose={closeDeleteConfirmationModal}
        onConfirm={() => {
          deleteCommentMutation.mutate();
          closeDeleteConfirmationModal();
        }}
        confirmText={t("delete")}
        confirmVariant="danger"
        title={t("courses.admin_summary.delete_comment.title")}
      >
        <div className="mb-6">
          {t("courses.admin_summary.delete_comment.description")}
        </div>
      </ConfirmationModal>
      <div className="group my-4 flex items-start justify-between">
        <NavLink
          to={coursePaths.showLessonComment({
            lessonId: comment.lesson.id,
            sectionId: comment.lesson.section_id,
            commentId: comment.id,
            parentMessageId: comment.parent_message_id,
            spaceSlug: space.slug,
          })}
        >
          <div className="mb-3">
            <Typography.LabelXs weight="medium">
              {comment.lesson.name}
            </Typography.LabelXs>
          </div>
          <div className="flex items-start">
            <UserImage
              name={comment.author.name}
              src={comment.author.avatar_url}
              size="8"
            />
            <div className="-mt-0.5 space-y-1 pl-3">
              <div
                className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-2"
                data-testid="course-header-actions"
              >
                <Typography.LabelMd weight="semibold">
                  {comment.author.name}
                </Typography.LabelMd>
                <Typography.LabelXs weight="medium">
                  {formatDateString(comment.created_at)}
                </Typography.LabelXs>
              </div>
              <Typography.BodyMd>{comment.body_text}</Typography.BodyMd>
            </div>
          </div>
        </NavLink>
        <Dropdown
          direction="bottom-end"
          button={
            <div
              className={classNames({
                "invisible group-hover:visible": canHover,
                visible: !canHover,
              })}
            >
              <Dropdown.MenuButton>
                <IconButton
                  name="16-menu-dots-horizontal"
                  iconSize={16}
                  ariaLabel={t("options_dropdown")}
                />
              </Dropdown.MenuButton>
            </div>
          }
          options={dropDownOptions}
        />
      </div>
    </>
  );
};
